<template>
  <div class="app-container">
    <Header />

    <div class="blog-content-container">
      <div :class="mainContentClass">
        <div class="blog-header">
          <span class="blog-date">{{ date }}</span>
          <h1 class="blog-title">{{ title }}</h1>
          <div class="blog-meta">
            <div class="author-info">
              <img
                :src="authorAvatar || '/blog_assets/logo.png'"
                class="author-avatar"
              />
              <span class="author-name">{{ author || "Skyhost Team" }}</span>
            </div>
          </div>
        </div>

        <div class="team-building-blog">
          <div class="blog-hero-image">
            <img :src="heroImage" alt="Blog hero image" class="hero-image" />
          </div>

          <section class="blog-section">
            <p>
              You've scheduled the weekly all‑hands, distributed the agenda,
              maybe even sent the donuts—yet everyone still logs off feeling
              like they've "been there, done that." What if your next team event
              could actually spark genuine excitement? Enter live drone tours: a
              fresh blend of real‑time exploration, team challenges, and clear
              business value. Here's how to make your remote culture come alive.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h2>1. Live Virtual Team Outings—Bond Over Shared Adventure</h2>
            <p>
              Imagine your group logging on from Seattle, London, and Bangalore
              as a drone lifts off over Hawaii's Koko Crater. You're not just
              watching a video; you're discovering together, swapping
              observations in real time.
            </p>
            <ul>
              <li>
                <strong>Why it works:</strong> Shared "wow" moments create
                natural conversation and inside jokes.
              </li>
              <li>
                <strong>Conversation starter:</strong> "Did you catch that
                rainbow over Hanauma Bay? I've never seen the water glow like
                that!"
              </li>
            </ul>
            <p>
              This kind of collective thrill replaces awkward icebreakers with
              genuine connection.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h2>2. Structured Engagement That Keeps Everyone Hooked</h2>
            <p>
              Too many virtual events feel like passive webinars. With Skyhost,
              every minute is an interactive exercise:
            </p>
            <ul>
              <li>
                <strong>Mini‑missions:</strong> "Spot the blow hole" challenges
                teams to coordinate clues and direct the drone.
              </li>
              <li>
                <strong>Live commentary:</strong> Our hosts weave in local lore
                and scientific tidbits—no PowerPoint in sight.
              </li>
              <li>
                <strong>Real collaboration:</strong> Teams vote on flight paths,
                camera angles, and photo‑hunt targets.
              </li>
            </ul>
            <p>
              Each element has a purpose: reinforcing communication, strategic
              thinking, and shared decision‑making.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h2>3. Skill Development Through Real‑World Scenarios</h2>
            <blockquote>
              "We need to practice working under pressure."
            </blockquote>
            <p>Sound familiar? During a drone photo‑hunt, your team will:</p>
            <ol>
              <li>Listen to concise instructions.</li>
              <li>Delegate roles (pilot, navigator, photographer).</li>
              <li>Execute under a ticking clock.</li>
            </ol>
            <p>
              Afterwards, a quick debrief ties these actions back to workplace
              skills—effective delegation, clear hand‑offs, and agile
              problem‑solving. It's team training disguised as an adventure.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h2>4. Global Experiences That Expand Perspectives</h2>
            <p>
              <strong>Ever wish you could show your team the world?</strong> Now
              you can:
            </p>
            <ul>
              <li>Tour the volcanic plains of Iceland.</li>
              <li>Drift above Peru's ancient ruins.</li>
              <li>Hover over Cape Town's colorful Bo‑Kaap.</li>
            </ul>
            <p>
              Each flight is a live window into another culture and ecosystem.
              As your colleagues compare landscapes and share reactions, you're
              also broadening their global awareness—essential for today's
              interconnected business world.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h2>5. Measurable Impact—Proof You Can Share with Leadership</h2>
            <p>
              HR leaders want results, not just anecdotes. After every session
              you'll receive:
            </p>
            <ul>
              <li>
                <strong>Engagement report:</strong> Who participated, how long
                they interacted, which challenges landed best.
              </li>
              <li>
                <strong>Skill highlights:</strong> Bullet‑point insights like
                "Teams improved consensus‑building by 30% in the photo‑hunt."
              </li>
              <li>
                <strong>Participant feedback:</strong> Quick quotes that capture
                excitement and key takeaways.
              </li>
            </ul>
            <p>
              With these metrics, you can demonstrate clear ROI—making it easy
              to secure budget for your next drone‑powered team event.
            </p>
          </section>

          <hr class="blog-divider" />

          <section class="blog-section">
            <h3>Ready to Rethink Remote Team Building?</h3>
            <p>
              Don't settle for another virtual happy hour. Give your team a
              shared adventure that strengthens collaboration, sharpens skills,
              and leaves everyone talking. Connect with Skyhost today to
              schedule a demo and discover how live drone tours can transform
              your remote culture.
            </p>
          </section>
        </div>

        <div class="blog-footer">
          <div class="blog-tags" v-if="tags && tags.length">
            <span class="tag-label">Tags:</span>
            <span v-for="(tag, index) in tags" :key="index" class="blog-tag">
              {{ tag }}
            </span>
          </div>

          <div class="share-section">
            <span class="share-label">Share this article:</span>
            <div class="share-icons">
              <vue-feather
                type="twitter"
                size="18"
                class="share-icon"
                @click="shareOnTwitter"
              ></vue-feather>
              <vue-feather
                type="facebook"
                size="18"
                class="share-icon"
                @click="shareOnFacebook"
              ></vue-feather>
              <vue-feather
                type="linkedin"
                size="18"
                class="share-icon"
                @click="shareOnLinkedIn"
              ></vue-feather>
              <vue-feather
                type="mail"
                size="18"
                class="share-icon"
                @click="shareByEmail"
              ></vue-feather>
            </div>
          </div>

          <div class="related-posts" v-if="relatedBlogs.length">
            <h3 class="related-title">Related articles</h3>
            <div class="related-grid">
              <div
                v-for="relatedBlog in relatedBlogs"
                :key="relatedBlog.id"
                class="related-card"
                @click="navigateToBlog(relatedBlog.id)"
              >
                <img
                  :src="relatedBlog.image"
                  alt="Related blog image"
                  class="related-image"
                />
                <div class="related-content">
                  <h4 class="related-blog-title">{{ relatedBlog.title }}</h4>
                  <span class="related-date">{{ relatedBlog.date }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="back-to-blogs">
            <button class="blog-btn" @click="navigateToBlogs">
              <vue-feather
                type="arrow-left"
                size="16"
                class="back-icon"
              ></vue-feather>
              Back to all blogs
            </button>
          </div>
        </div>
      </div>
    </div>

    <Footer />
  </div>
</template>

<script>
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import Footer from "@/components/Footer.vue";
import { blogsRegistry } from "@/data/blogRegistry";

export default {
  name: "TeamBuildingBlog",
  components: {
    Header,
    Footer,
  },
  data() {
    const blogId = "team-building-tips";
    const blog = blogsRegistry[blogId];

    return {
      id: blogId,
      title: blog.title,
      date: blog.date,
      author: blog.author,
      authorAvatar: blog.authorAvatar,
      image: blog.image,
      heroImage: blog.image,
      excerpt: blog.excerpt,
      tags: blog.tags,
      relatedBlogs: [],
    };
  },
  computed: {
    isMobileLayout() {
      return isMobile();
    },
    currentPageUrl() {
      return `${window.location.origin}${this.$route.fullPath}`;
    },
  },
  methods: {
    navigateToBlog(blogId) {
      this.$router.push({
        path: `/blog/${blogId}`,
      });
    },
    shareOnTwitter() {
      const text = `${this.title} - Check out this article by Skyhost`;
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
        text
      )}&url=${encodeURIComponent(this.currentPageUrl)}`;
      window.open(url, "_blank");
    },
    shareOnFacebook() {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareOnLinkedIn() {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
        this.currentPageUrl
      )}`;
      window.open(url, "_blank");
    },
    shareByEmail() {
      const subject = encodeURIComponent(this.title);
      const body = encodeURIComponent(
        `I thought you might find this article interesting: ${this.title}\n\n${this.currentPageUrl}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;
    },
    navigateToBlogs() {
      this.$router.push({
        path: "/blogs",
      });
    },
  },
  created() {
    // Get related blogs (excluding current blog)
    this.relatedBlogs = Object.values(blogsRegistry)
      .filter((blog) => blog.id !== this.id)
      .slice(0, 2); // Limit to 2 related blogs
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-blog-content" : "web-blog-content";
    });

    return {
      mainContentClass,
    };
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
  color: #333;
}

.blog-content-container {
  margin-top: 5rem;
}

.web-blog-content {
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
}

.mobile-blog-content {
  padding: 1.5rem;
}

.blog-header {
  margin-bottom: 2rem;
}

.blog-date {
  color: #666;
  font-size: 1rem;
}

.blog-title {
  font-size: 2.5rem;
  margin: 1rem 0;
  color: #000;
  line-height: 1.3;
}

.blog-meta {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.author-info {
  display: flex;
  align-items: center;
}

.author-avatar {
  width: 20px;
  height: 20px;
  /* border-radius: 50%; */
  margin-right: 0.8rem;
  object-fit: contain;
}

.author-name {
  font-weight: 500;
  color: #333;
}

.blog-hero-image {
  margin-bottom: 2rem;
  border-radius: 8px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  max-height: 500px;
  border-radius: 8px;
}

.team-building-blog {
  line-height: 1.8;
  color: #333;
}

.blog-section {
  margin-bottom: 2rem;
}

.blog-divider {
  margin: 2.5rem 0;
  border: none;
  border-top: 1px solid #eee;
}

h2 {
  font-size: 2rem;
  margin: 1.5rem 0;
  color: #000;
  font-weight: 600;
}

h3 {
  font-size: 1.4rem;
  margin: 1.5rem 0 1rem;
  color: #000;
}

p {
  margin-bottom: 1.5rem;
}

ul,
ol {
  margin: 1rem 0 1.5rem 1.5rem;
}

li {
  margin-bottom: 0.5rem;
}

strong {
  font-weight: 600;
}

blockquote {
  margin: 2rem 0;
  padding: 1rem 1.5rem;
  border-left: 4px solid #000;
  background-color: #f8f9fa;
  font-style: italic;
  color: #555;
  font-size: 1.2rem;
}

.blog-footer {
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
}

.blog-tags {
  margin-bottom: 2rem;
}

.tag-label {
  font-weight: 500;
  margin-right: 0.5rem;
}

.blog-tag {
  display: inline-block;
  padding: 0.3rem 0.8rem;
  margin: 0.3rem;
  background-color: #f0f0f0;
  border-radius: 20px;
  font-size: 0.9rem;
  color: #666;
}

.share-section {
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
}

.share-label {
  font-weight: 500;
  margin-right: 1rem;
}

.share-icons {
  display: flex;
  gap: 1rem;
}

.share-icon {
  cursor: pointer;
  color: #555;
}

.share-icon:hover {
  color: #1565c0;
}

.related-posts {
  margin-bottom: 3rem;
}

.related-title {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #000;
}

.related-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
}

.related-card {
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: transform 0.3s ease;
}

.related-card:hover {
  transform: translateY(-3px);
}

.related-image {
  width: 100%;
  height: 150px;
  object-fit: cover;
}

.related-content {
  padding: 1rem;
}

.related-blog-title {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #000;
}

.related-date {
  font-size: 0.8rem;
  color: #666;
}

.back-to-blogs {
  margin-top: 3rem;
  text-align: center;
}

.blog-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.8rem 1.5rem;
  background-color: #f0f0f0;
  border: none;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.blog-btn:hover {
  background-color: #e0e0e0;
}

.back-icon {
  margin-right: 0.5rem;
}

/* Mobile styles */
@media (max-width: 768px) {
  .blog-title {
    font-size: 2rem;
  }

  .related-grid {
    grid-template-columns: 1fr;
  }
}
</style>
